/**
 * MakeAppointmentModal - Unified Appointment Creation Modal
 *
 * Port   : Date + Start Time + End Time + Gate Code / PIN / Container Location
 *          / Appointment Number  (only date is required — times are optional,
 *          a missing start time is stored as 00:00 by the backend)
 * Warehouse: Date + Time + Appointment Number / PIN / Container Location
 *
 * Source badge in the header reflects how the existing appointment was
 * captured (Via eModal vs Manual), so dispatchers can tell first-party
 * bookings apart from external recordings when editing.
 *
 * Uses a custom time slot dropdown (30-min intervals) instead of native time picker.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import {
  X,
  Calendar,
  Clock,
  Ship,
  Warehouse,
  Hash,
  Key,
  MapPin,
  AlertCircle,
  ChevronDown
} from 'lucide-react'

type AppointmentModalType = 'port' | 'warehouse'
export type AppointmentSource = 'manual' | 'emodal'

export interface PortAppointmentData {
  date: string
  startTime: string
  endTime: string
  /** 「当天，不限时间点」——勾上时时间字段一律为空 */
  allDay: boolean
  gateCode?: string
  pin?: string
  containerLocation?: string
  appointmentNumber?: string
}

export interface WarehouseAppointmentData {
  date: string
  time: string
  /** 见 PortAppointmentData */
  allDay: boolean
  appointmentNumber?: string
  pin?: string
  containerLocation?: string
}

interface MakeAppointmentModalProps {
  open: boolean
  onClose: () => void
  type: AppointmentModalType
  locationName?: string
  locationCity?: string
  /** Source of the existing appointment (only shown in edit mode). */
  source?: AppointmentSource | null
  initialData?: {
    date?: string
    startTime?: string
    endTime?: string
    time?: string
    allDay?: boolean
    gateCode?: string
    pin?: string
    containerLocation?: string
    appointmentNumber?: string
  }
  onSave: (data: PortAppointmentData | WarehouseAppointmentData) => void
  isSaving?: boolean
}

// ─── Time slot generation ──────────────────────────────────────────────
// Generate time slots every 30 minutes from 00:00 to 23:30
const SLOT_STEP_MINUTES = 30
const TIME_SLOT_VALUES: string[] = (() => {
  const values: string[] = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += SLOT_STEP_MINUTES) {
      values.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`)
    }
  }
  return values
})()

// Normalize "H:MM" / "HH:MM" to 24-hour display "HH:MM"
const formatTimeDisplay = (time: string): string => {
  if (!time) return ''
  const [hStr, mStr] = time.split(':')
  const h = parseInt(hStr, 10)
  const m = mStr || '00'
  if (isNaN(h)) return time
  return `${h.toString().padStart(2, '0')}:${m}`
}

// ─── TimeSlotPicker component ──────────────────────────────────────────
interface TimeSlotPickerProps {
  id: string
  value: string
  onChange: (val: string) => void
  hasError?: boolean
  accentColor: string
  placeholder?: string
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  id,
  value,
  onChange,
  hasError,
  accentColor,
  placeholder = 'Select time'
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // 已存在的预约可能落在非半点上（旧的 15 分钟间隔或 eModal 回填），
  // 把它插回列表，编辑时才看得到当前选中项。
  const slots = useMemo(() => {
    const values = !value || TIME_SLOT_VALUES.includes(value)
      ? TIME_SLOT_VALUES
      : [...TIME_SLOT_VALUES, value].sort((a, b) => a.localeCompare(b))
    return values.map(v => ({ value: v, label: formatTimeDisplay(v) }))
  }, [value])

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen])

  // Scroll to selected or nearest time when opening
  useEffect(() => {
    if (!isOpen || !listRef.current) return
    let targetIndex = 0
    if (value) {
      const idx = slots.findIndex(s => s.value === value)
      targetIndex = idx >= 0 ? idx : 0
    } else {
      // Scroll to ~8:00 AM as reasonable default
      targetIndex = slots.findIndex(s => s.value === '08:00')
    }
    const item = listRef.current.children[targetIndex] as HTMLElement
    if (item) {
      item.scrollIntoView({ block: 'center' })
    }
  }, [isOpen, value, slots])

  const handleSelect = useCallback((val: string) => {
    onChange(val)
    setIsOpen(false)
  }, [onChange])

  const displayValue = formatTimeDisplay(value)

  return (
    <div ref={containerRef} className="relative" id={id}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 w-full border rounded-lg px-3 h-11 bg-white
          transition-all duration-150 cursor-pointer text-left
          ${hasError
            ? 'border-red-300 ring-2 ring-red-100'
            : isOpen
              ? 'ring-2 border-transparent'
              : 'border-gray-200 hover:border-gray-300'
          }
        `}
        style={isOpen && !hasError ? { ringColor: accentColor, borderColor: accentColor, boxShadow: `0 0 0 2px ${accentColor}30` } : undefined}
      >
        <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className={`flex-1 text-sm ${displayValue ? 'text-gray-900' : 'text-gray-400'}`}>
          {displayValue || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          ref={listRef}
          className="absolute z-[110] top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[240px] overflow-y-auto py-1"
          style={{ scrollbarWidth: 'thin' }}
        >
          {slots.map(slot => {
            const isSelected = slot.value === value
            // Mark hour boundaries
            const isHourMark = slot.value.endsWith(':00')
            return (
              <button
                key={slot.value}
                type="button"
                onClick={() => handleSelect(slot.value)}
                className={`
                  w-full px-3 py-2 text-sm text-left cursor-pointer transition-colors duration-75
                  ${isSelected
                    ? 'text-white font-medium'
                    : isHourMark
                      ? 'text-gray-900 font-medium hover:bg-gray-50'
                      : 'text-gray-600 hover:bg-gray-50'
                  }
                `}
                style={isSelected ? { backgroundColor: accentColor } : undefined}
              >
                {slot.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────
// These must all read the *local* calendar. `toISOString()` returns the UTC
// day, so it disagrees with extractTime() below (which reads local hours)
// whenever the local offset pushes across midnight — that mismatch put a date
// and a time from two different timezones side by side in this form, and any
// re-save then wrote the mixture back to the database.
const toLocalDateString = (d: Date): string => {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const getTodayDate = (): string => toLocalDateString(new Date())

const extractDate = (datetime?: string): string => {
  if (!datetime) return ''
  const d = new Date(datetime)
  return isNaN(d.getTime()) ? '' : toLocalDateString(d)
}

const extractTime = (datetime?: string): string => {
  if (!datetime) return ''
  const date = new Date(datetime)
  if (isNaN(date.getTime())) return ''
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

// ─── Main modal ─────────────────────────────────────────────────────────
export const MakeAppointmentModal: React.FC<MakeAppointmentModalProps> = ({
  open,
  onClose,
  type,
  locationName,
  locationCity,
  source,
  initialData,
  onSave,
  isSaving = false
}) => {
  const [date, setDate] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [allDay, setAllDay] = useState(false)
  const [gateCode, setGateCode] = useState('')
  const [appointmentNumber, setAppointmentNumber] = useState('')
  const [pin, setPin] = useState('')
  const [containerLocation, setContainerLocation] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  // 这次打开之后操作员有没有动过表单。
  // `initialData` 是父组件内联算出来的，每次重渲染都是一个新对象，而派单看板
  // 只要收到一条 task_updated（自己刚存完码头预约也会广播给自己）就整块重载 ——
  // 于是这个 effect 在人还在选日期的时候重跑，把刚填的内容恢复成"原样"，
  // 点 Save 就报「Date is required」，重填一遍才成。
  // 数据还没到就打开弹窗的情况仍然要回填，所以判据是"人动过没有"，不是"跑过没有"。
  const touchedRef = useRef(false)

  useEffect(() => {
    if (!open) {
      touchedRef.current = false
      return
    }
    if (touchedRef.current) return

    if (initialData) {
      const wasAllDay = !!initialData.allDay
      setDate(initialData.date || extractDate(initialData.startTime) || '')
      // An all-day booking is stored anchored to 00:00; showing that back as a
      // selected time slot would turn it into a real midnight appointment the
      // moment the operator hits Save.
      setStartTime(!wasAllDay && initialData.startTime ? extractTime(initialData.startTime) : '')
      setEndTime(!wasAllDay && initialData.endTime ? extractTime(initialData.endTime) : '')
      setAllDay(wasAllDay)
      setGateCode(initialData.gateCode || '')
      setAppointmentNumber(initialData.appointmentNumber || '')
      setPin(initialData.pin || '')
      setContainerLocation(initialData.containerLocation || '')
    } else {
      setDate('')
      setStartTime('')
      setEndTime('')
      setAllDay(false)
      setGateCode('')
      setAppointmentNumber('')
      setPin('')
      setContainerLocation('')
    }
    setErrors({})
  }, [open, initialData])

  // 任何一次输入都算"动过"——从这一刻起，外部刷新不再回灌 initialData。
  const markTouched = () => { touchedRef.current = true }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!date) {
      newErrors.date = 'Date is required'
    }

    // Times are optional — only the ordering between them is enforced.
    // All Day has no times to order.
    if (!allDay && type === 'port' && startTime && endTime && endTime <= startTime) {
      newErrors.endTime = 'End time must be after start time'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = () => {
    if (!validate()) return

    if (type === 'port') {
      onSave({
        date,
        // All Day means the day is the whole booking — send no window at all,
        // rather than a window the backend would have to know to ignore.
        startTime: allDay ? '' : startTime,
        endTime: allDay ? '' : endTime,
        allDay,
        gateCode: gateCode || undefined,
        pin: pin || undefined,
        containerLocation: containerLocation || undefined,
        appointmentNumber: appointmentNumber || undefined,
      } as PortAppointmentData)
    } else {
      onSave({
        date,
        time: allDay ? '' : startTime,
        allDay,
        appointmentNumber: appointmentNumber || undefined,
        pin: pin || undefined,
        containerLocation: containerLocation || undefined,
      } as WarehouseAppointmentData)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
  }

  if (!open) return null

  const isPort = type === 'port'
  const accentColor = isPort ? '#1976D2' : '#F57C00'
  const accentBg = isPort ? 'bg-blue-50' : 'bg-orange-50'
  const accentText = isPort ? 'text-blue-600' : 'text-orange-600'
  const accentBorder = isPort ? 'border-blue-200' : 'border-orange-200'
  const accentRing = isPort ? 'focus-within:ring-blue-500 focus-within:border-blue-400' : 'focus-within:ring-orange-500 focus-within:border-orange-400'
  const Icon = isPort ? Ship : Warehouse
  const title = isPort ? 'Port Appointment' : 'Warehouse Appointment'

  const inputWrapperBase = `flex items-center gap-2 border rounded-lg px-3 h-11 bg-white transition-all duration-150 ring-0 ring-offset-0 ${accentRing} focus-within:ring-2`
  const inputBase = 'flex-1 bg-transparent border-none outline-none text-sm text-gray-900 placeholder:text-gray-400 h-full'
  const errorInputBorder = 'border-red-300 focus-within:ring-red-500 focus-within:border-red-400'

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      onKeyDown={handleKeyDown}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-[440px] mx-4 overflow-visible animate-in fade-in zoom-in-95 duration-200">
        {/* Top accent bar */}
        <div className="h-1 rounded-t-xl" style={{ backgroundColor: accentColor }} />

        {/* Header */}
        <div className="px-6 pt-5 pb-4 flex items-start justify-between">
          <div className="flex items-center gap-3.5">
            <div className={`w-10 h-10 rounded-lg ${accentBg} ${accentBorder} border flex items-center justify-center flex-shrink-0`}>
              <Icon className={`w-5 h-5 ${accentText}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-gray-900 leading-tight">{title}</h3>
                {source && (
                  <span
                    className={
                      source === 'emodal'
                        ? 'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100'
                        : 'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 border border-gray-200'
                    }
                    title={source === 'emodal' ? 'Captured from eModal receipt' : 'Recorded manually'}
                  >
                    {source === 'emodal' ? 'Via eModal' : 'Manual'}
                  </span>
                )}
              </div>
              {locationName && (
                <p className="text-sm text-gray-500 mt-0.5 leading-tight">
                  {locationName}
                  {locationCity && <span className="text-gray-400"> · {locationCity}</span>}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 -mr-1.5 -mt-0.5 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="w-4.5 h-4.5 text-gray-400" />
          </button>
        </div>

        {/* Divider */}
        <div className="mx-6 border-t border-gray-100" />

        {/* Content */}
        <div className="px-6 py-5 space-y-5">
          {/* Date Field */}
          <div>
            <label htmlFor="appt-date" className="flex items-center gap-1.5 mb-1.5 text-sm font-medium text-gray-700">
              Date <span className="text-red-400">*</span>
            </label>
            <div className={`${inputWrapperBase} ${errors.date ? errorInputBorder : 'border-gray-200'}`}>
              <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <input
                id="appt-date"
                type="date"
                value={date}
                onChange={(e) => { markTouched(); setDate(e.target.value); setErrors(prev => ({ ...prev, date: '' })) }}
                min={getTodayDate()}
                className={`${inputBase} [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
              />
            </div>
            {errors.date && (
              <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5">
                <AlertCircle className="w-3 h-3" />
                {errors.date}
              </p>
            )}
          </div>

          {/* All Day — the facility gave us a day, not a window.
              Ticking it clears the times so the row can't carry both. */}
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              id="appt-all-day"
              type="checkbox"
              checked={allDay}
              onChange={(e) => {
                markTouched()
                const next = e.target.checked
                setAllDay(next)
                if (next) { setStartTime(''); setEndTime('') }
                setErrors(prev => ({ ...prev, startTime: '', endTime: '', time: '' }))
              }}
              className="w-4 h-4 rounded border-gray-300 cursor-pointer"
              style={{ accentColor }}
            />
            <span className="text-sm font-medium text-gray-700">All Day</span>
            <span className="text-xs font-normal text-gray-400">No fixed time slot</span>
          </label>

          {/* Time Fields — custom time slot picker */}
          {allDay ? (
            <div className="flex items-center gap-2 border border-dashed border-gray-200 rounded-lg px-3 h-11 bg-gray-50">
              <Clock className="w-4 h-4 text-gray-300 flex-shrink-0" />
              <span className="text-sm text-gray-400">
                All Day — no time slot for this {isPort ? 'terminal' : 'warehouse'} visit
              </span>
            </div>
          ) : isPort ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="flex items-center gap-1.5 mb-1.5 text-sm font-medium text-gray-700">
                  Start Time
                </label>
                <TimeSlotPicker
                  id="start-time"
                  value={startTime}
                  onChange={(val) => { markTouched(); setStartTime(val); setErrors(prev => ({ ...prev, startTime: '' })) }}
                  hasError={!!errors.startTime}
                  accentColor={accentColor}
                  placeholder="Start"
                />
                {errors.startTime && (
                  <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5">
                    <AlertCircle className="w-3 h-3" />
                    {errors.startTime}
                  </p>
                )}
              </div>
              <div>
                <label className="flex items-center gap-1.5 mb-1.5 text-sm font-medium text-gray-700">
                  End Time
                </label>
                <TimeSlotPicker
                  id="end-time"
                  value={endTime}
                  onChange={(val) => { markTouched(); setEndTime(val); setErrors(prev => ({ ...prev, endTime: '' })) }}
                  hasError={!!errors.endTime}
                  accentColor={accentColor}
                  placeholder="End"
                />
                {errors.endTime && (
                  <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5">
                    <AlertCircle className="w-3 h-3" />
                    {errors.endTime}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div>
              <label className="flex items-center gap-1.5 mb-1.5 text-sm font-medium text-gray-700">
                Time
              </label>
              <TimeSlotPicker
                id="appt-time"
                value={startTime}
                onChange={(val) => { markTouched(); setStartTime(val); setErrors(prev => ({ ...prev, time: '' })) }}
                hasError={!!errors.time}
                accentColor={accentColor}
                placeholder="Select time"
              />
              {errors.time && (
                <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5">
                  <AlertCircle className="w-3 h-3" />
                  {errors.time}
                </p>
              )}
            </div>
          )}

          {/* Divider before optional fields */}
          <div className="border-t border-dashed border-gray-200" />

          {/* Optional fields
              Port:      Appointment Number │ Gate Code
                         PIN                │ Container Location
              Warehouse: Appointment Number │ PIN
                         Container Location │ (empty)
              All four are surfaced in parity with the eModal receipt + the
              tasks flat columns so Edit mode doesn't drop info. */}
          <div className="grid grid-cols-2 gap-3">
            {/* Appointment Number */}
            <div>
              <label htmlFor="appt-number" className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
                Appt Number
                <span className="text-xs font-normal text-gray-400">Optional</span>
              </label>
              <div className={`${inputWrapperBase} border-gray-200`}>
                <Hash className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  id="appt-number"
                  type="text"
                  value={appointmentNumber}
                  onChange={(e) => { markTouched(); setAppointmentNumber(e.target.value) }}
                  placeholder={isPort ? 'TcktNr' : 'e.g. APT-...'}
                  className={`${inputBase} font-mono tracking-wide`}
                />
              </div>
            </div>

            {/* Gate Code (port) OR PIN (warehouse) */}
            {isPort ? (
              <div>
                <label htmlFor="gate-code" className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
                  Gate Code
                  <span className="text-xs font-normal text-gray-400">Optional</span>
                </label>
                <div className={`${inputWrapperBase} border-gray-200`}>
                  <Hash className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <input
                    id="gate-code"
                    type="text"
                    value={gateCode}
                    onChange={(e) => { markTouched(); setGateCode(e.target.value) }}
                    placeholder="e.g. G-142"
                    className={`${inputBase} font-mono tracking-wide`}
                  />
                </div>
              </div>
            ) : (
              <div>
                <label htmlFor="appt-pin" className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
                  PIN
                  <span className="text-xs font-normal text-gray-400">Optional</span>
                </label>
                <div className={`${inputWrapperBase} border-gray-200`}>
                  <Key className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <input
                    id="appt-pin"
                    type="text"
                    value={pin}
                    onChange={(e) => { markTouched(); setPin(e.target.value) }}
                    placeholder="PIN"
                    className={`${inputBase} font-mono tracking-wide`}
                  />
                </div>
              </div>
            )}

            {/* Port row 2: PIN */}
            {isPort && (
              <div>
                <label htmlFor="appt-pin" className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
                  PIN
                  <span className="text-xs font-normal text-gray-400">Optional</span>
                </label>
                <div className={`${inputWrapperBase} border-gray-200`}>
                  <Key className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <input
                    id="appt-pin"
                    type="text"
                    value={pin}
                    onChange={(e) => { markTouched(); setPin(e.target.value) }}
                    placeholder="PIN"
                    className={`${inputBase} font-mono tracking-wide`}
                  />
                </div>
              </div>
            )}

            {/* Container Location (both) */}
            <div className={isPort ? '' : 'col-span-2'}>
              <label htmlFor="appt-loc" className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
                Container Location
                <span className="text-xs font-normal text-gray-400">Optional</span>
              </label>
              <div className={`${inputWrapperBase} border-gray-200`}>
                <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <input
                  id="appt-loc"
                  type="text"
                  value={containerLocation}
                  onChange={(e) => { markTouched(); setContainerLocation(e.target.value) }}
                  placeholder={isPort ? 'e.g. H1002' : 'e.g. Dock 5'}
                  className={`${inputBase} font-mono tracking-wide`}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50/80 border-t border-gray-100 flex items-center justify-end gap-2.5">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 h-9 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all duration-150 disabled:opacity-50 cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-5 h-9 text-sm font-medium text-white rounded-lg transition-all duration-150 disabled:opacity-60 cursor-pointer flex items-center gap-2"
            style={{ backgroundColor: accentColor }}
            onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(0.9)'}
            onMouseLeave={(e) => e.currentTarget.style.filter = 'none'}
          >
            {isSaving ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              'Save Appointment'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
